import React, {useState} from "react";
import Modal from "react-bootstrap/Modal";
import {Button, TextField, ToggleButton, ToggleButtonGroup, Typography} from "@mui/material";
import {BugReport, Lightbulb, ChatBubbleOutline} from "@mui/icons-material";
import Swal from "sweetalert2";

const DARK = 'var(--lm-bg-1a1f2b)';
const BORDER = 'rgba(var(--th-6c4f9b-rgb), 0.4)';
const ACCENT = 'var(--lm-tx-th-c9a0f0)';

const SUMMARY_MAX = 200;
const DETAILS_MAX = 5000;

// Mirrors the server's FeedbackRequest.type. Sent as-is, so these strings and the ones the
// backend puts in the subject line are the same vocabulary.
const TYPES = [
    {value: 'BUG', label: 'Bug', icon: <BugReport sx={{fontSize: 17}}/>},
    {value: 'IDEA', label: 'Idea', icon: <Lightbulb sx={{fontSize: 17}}/>},
    {value: 'OTHER', label: 'Other', icon: <ChatBubbleOutline sx={{fontSize: 17}}/>},
];

const fieldSx = {
    mb: 2,
    '& .MuiInputBase-root': {color: 'var(--lm-tx-f5f5f5)', fontSize: '0.85rem', backgroundColor: 'rgba(var(--lm-veil-rgb),0.04)'},
    '& .MuiInputLabel-root': {color: 'rgba(var(--lm-tx-f5f5f5-rgb), var(--lm-tx-a55))', fontSize: '0.85rem'},
    '& .MuiInputLabel-root.Mui-focused': {color: ACCENT},
    '& .MuiOutlinedInput-notchedOutline': {borderColor: BORDER},
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {borderColor: 'rgba(var(--th-6c4f9b-rgb), 0.7)'},
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {borderColor: ACCENT},
    '& .MuiFormHelperText-root': {color: 'rgba(var(--lm-tx-f5f5f5-rgb), var(--lm-tx-a40))'},
};

/**
 * In-app bug report / suggestion form, opened from the header's account menu.
 *
 * The context block at the bottom is the point of doing this in-app rather than pointing
 * users at an email address: page, browser and active campaign arrive with every report
 * without anyone having to remember to include them. It is shown rather than merely
 * attached, because silently harvesting even harmless context is not something to spring
 * on someone hitting Send.
 */
export default function FeedbackModal({show, onHide, campaignName}) {
    const [type, setType] = useState('BUG');
    const [summary, setSummary] = useState('');
    const [details, setDetails] = useState('');
    const [sending, setSending] = useState(false);
    const [touched, setTouched] = useState(false);

    const summaryError = touched && !summary.trim();

    const reset = () => {
        setType('BUG');
        setSummary('');
        setDetails('');
        setTouched(false);
    };

    const close = () => {
        if (sending) return;
        reset();
        onHide();
    };

    async function submit() {
        setTouched(true);
        if (!summary.trim() || sending) return;

        const userJSON = JSON.parse(sessionStorage.getItem('user') || 'null');
        if (!userJSON) return;

        setSending(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_PROXY}/feedback`, {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + userJSON.accessToken,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    type,
                    summary: summary.trim(),
                    details: details.trim(),
                    // window.location rather than react-router: this modal lives in the
                    // header, above any single route, and the raw path is what we want.
                    page: window.location.pathname + window.location.search,
                    userAgent: navigator.userAgent,
                    campaignName: campaignName || null,
                    appVersion: process.env.REACT_APP_VERSION || null,
                }),
            });

            if (!res.ok) {
                // 429 and 502 both carry a plain-text body written for the user.
                const message = await res.text().catch(() => '');
                throw new Error(message || 'Your report could not be sent. Please try again.');
            }

            reset();
            onHide();
            Swal.fire({
                timer: 2600, title: 'Thank you', icon: 'success', position: 'bottom-end',
                text: type === 'BUG' ? "Your report is on its way to us." : "Your feedback is on its way to us.",
                showConfirmButton: false, toast: true,
            });
        } catch (err) {
            Swal.fire({
                timer: 3500, title: 'Not sent', icon: 'error', position: 'bottom-end',
                text: err.message, showConfirmButton: false, toast: true,
            });
        } finally {
            setSending(false);
        }
    }

    return (
        <Modal show={show} onHide={close} centered scrollable data-bs-theme="dark">
            <Modal.Header closeButton style={{
                backgroundColor: DARK,
                borderBottom: `1px solid ${BORDER}`,
                background: `radial-gradient(ellipse at 50% 0%, rgba(var(--th-6c4f9b-rgb), 0.35) 0%, ${DARK} 80%)`,
                alignItems: 'center', paddingTop: 12, paddingBottom: 12,
            }}>
                <BugReport style={{fontSize: 26, color: ACCENT, filter: 'drop-shadow(0 0 6px rgba(var(--th-c9a0f0-rgb), 0.5))', marginRight: 10}}/>
                <Modal.Title style={{color: ACCENT, fontWeight: 700, letterSpacing: '0.02em', fontSize: '1.05rem'}}>
                    Send Feedback
                </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{backgroundColor: DARK, color: 'var(--lm-tx-f5f5f5)', padding: '18px 24px'}}>
                <ToggleButtonGroup
                    exclusive
                    value={type}
                    onChange={(e, next) => next && setType(next)}
                    size="small"
                    sx={{
                        mb: 2,
                        '& .MuiToggleButton-root': {
                            color: 'rgba(var(--lm-tx-f5f5f5-rgb), var(--lm-tx-a60))', borderColor: BORDER, textTransform: 'none',
                            fontSize: '0.8rem', gap: 0.75, px: 1.75,
                        },
                        '& .MuiToggleButton-root.Mui-selected': {
                            color: ACCENT, backgroundColor: 'rgba(var(--th-6c4f9b-rgb), 0.25)',
                            '&:hover': {backgroundColor: 'rgba(var(--th-6c4f9b-rgb), 0.35)'},
                        },
                    }}
                >
                    {TYPES.map(t => (
                        <ToggleButton key={t.value} value={t.value}>{t.icon}{t.label}</ToggleButton>
                    ))}
                </ToggleButtonGroup>

                <TextField
                    fullWidth size="small" label="Summary" sx={fieldSx}
                    value={summary}
                    onChange={e => setSummary(e.target.value.slice(0, SUMMARY_MAX))}
                    onBlur={() => setTouched(true)}
                    error={summaryError}
                    helperText={summaryError
                        ? 'A one-line summary is required.'
                        : `${summary.length}/${SUMMARY_MAX}`}
                    placeholder={type === 'BUG' ? 'Tokens snap to the wrong square' : 'What would you like to see?'}
                />

                <TextField
                    fullWidth multiline minRows={4} maxRows={10} size="small" sx={fieldSx}
                    label={type === 'BUG' ? 'What happened, and what did you expect?' : 'Details'}
                    value={details}
                    onChange={e => setDetails(e.target.value.slice(0, DETAILS_MAX))}
                    helperText={`${details.length}/${DETAILS_MAX}`}
                    placeholder={type === 'BUG'
                        ? 'Steps to reproduce it, if you know them.'
                        : 'Optional, but the more the better.'}
                />

                <div style={{
                    border: `1px solid ${BORDER}`, borderRadius: 8,
                    padding: '10px 12px', background: 'rgba(var(--lm-veil-rgb),0.02)',
                }}>
                    <Typography sx={{color: 'rgba(var(--lm-tx-f5f5f5-rgb), var(--lm-tx-a45))', fontSize: 11, letterSpacing: 0.4, mb: 0.5}}>
                        SENT WITH YOUR REPORT
                    </Typography>
                    <Typography sx={{color: 'rgba(var(--lm-tx-f5f5f5-rgb), var(--lm-tx-a60))', fontSize: 11.5, lineHeight: 1.6}}>
                        Your username and email (so we can reply), the page you're on
                        {campaignName ? `, your active campaign (${campaignName})` : ''}, and your browser version.
                    </Typography>
                </div>
            </Modal.Body>

            <Modal.Footer style={{backgroundColor: DARK, borderTop: `1px solid ${BORDER}`}}>
                <Button onClick={close} disabled={sending}
                        sx={{color: 'rgba(var(--lm-tx-f5f5f5-rgb), var(--lm-tx-a60))', textTransform: 'none'}}>
                    Cancel
                </Button>
                <Button onClick={submit} variant="contained" disabled={sending || !summary.trim()}
                        sx={{
                            textTransform: 'none', backgroundColor: 'var(--th-6c4f9b)',
                            '&:hover': {backgroundColor: 'var(--th-7b5fb5)'},
                            '&.Mui-disabled': {backgroundColor: 'rgba(var(--th-6c4f9b-rgb), 0.3)', color: 'rgba(245,245,245,0.35)'},
                        }}>
                    {sending ? 'Sending...' : 'Send'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
